// Editing a recipe alarm without its recipe's limits.
//
// A recipe alarm's editor could only ever offer what the recipe exposes: its
// thresholds, severity and modes. Changing what it watches, or giving it a
// qualifier the recipe cannot express, meant deleting the alarm and building a
// new one — which throws away its id, its history, and every notification tied
// to it. "I should be able to edit everything about that alarm" is the whole
// point of this module.
//
// The move is to open the SAME alarm in the custom builder, seeded with what it
// already does. That translation cannot be done here: it needs the compiler and
// the boat's catalog to know which instance the recipe bound and what hysteresis
// it applies. The cloud does it (`GET .../as-custom`) and this module consumes
// the answer.
//
// CONVERTING IS NOT AUTOMATIC. An alarm stays its recipe until the operator
// actually changes something the recipe cannot say. Renaming it, or watching it
// in one more mode, must not quietly turn "House battery low" into a hand-built
// alarm — that would free the recipe slot, change what the library offers, and
// surprise someone who only fixed a typo. {@link toUnlockPatchBody} is where
// that line is drawn.

import type { AlarmDefView } from './alarmDisplay';
import { draftConditions, initFromCustomDef, type CustomAlarmDraft } from './customAlarmForm';

/** `GET /v1/boats/:boatId/alerts/:alertId/as-custom` — the cloud's translation. */
export interface AsCustomResponse {
  readonly custom: { v: number; path: string; triggerClass: string };
  readonly thresholds: Record<string, number>;
  /** Present when the alarm carries qualifiers that survive as explicit clauses. */
  readonly conditions?: { logic: 'all' | 'any'; clauses: unknown[] };
}

/**
 * The recipe alarm read into the custom builder's draft.
 *
 * Built by handing {@link initFromCustomDef} a view of the SAME definition with
 * the translation's blocks overlaid — so the name, severity, modes and notify
 * setting come from the real stored alarm and only the trigger comes from the
 * translation. `null` when this build cannot represent it (the same guard the
 * custom editor already applies to a stored custom alarm), in which case the
 * caller must not offer the editor at all.
 */
export function unlockedDraft(
  def: AlarmDefView,
  translated: AsCustomResponse
): CustomAlarmDraft | null {
  // The custom vocabulary carries ONE threshold block (v1), so the translation's
  // numbers go under every enabled mode — which is what `initFromCustomDef`
  // reads back and what the API stores.
  const thresholdsByMode: Record<string, Record<string, number>> = {};
  const modes = def.enabledModes.length > 0 ? def.enabledModes : ['Underway'];
  for (const mode of modes) thresholdsByMode[mode] = { ...translated.thresholds };
  return initFromCustomDef({
    ...def,
    custom: translated.custom,
    thresholdsByMode,
    // The RESOLVED clauses, not the stored ones: a recipe's `engine_running`
    // preset is not a clause a custom alarm may carry, and the cloud has already
    // turned it into the concrete RPM clause that means the same thing.
    conditions: translated.conditions ?? null,
  });
}

/**
 * The modes whose stored numbers would be FLATTENED by converting, or `[]`.
 *
 * A recipe alarm may hold a different number per mode (a longer debounce in
 * Maintenance, say). A custom alarm holds ONE set of numbers for every mode it
 * watches (v1 compiles a single rule), so converting keeps the first mode's
 * values and drops the rest. That is a real loss and the operator has to be told
 * BEFORE they save — it was found by converting a real alarm and noticing its
 * 90-second Maintenance debounce come back as 60.
 *
 * Returns the modes that carry something different from the first enabled one.
 */
export function modesFlattenedByConversion(def: AlarmDefView): string[] {
  const modes = def.enabledModes;
  const first = modes[0];
  if (first === undefined) return [];
  const primary = def.thresholdsByMode[first] ?? {};
  const differs = (block: Record<string, unknown>): boolean => {
    const keys = new Set([...Object.keys(primary), ...Object.keys(block)]);
    for (const key of keys) {
      const a = primary[key];
      const b = block[key];
      // Only NUMBERS are compared: a key missing from one block is a gap in the
      // stored row, not an operator's deliberate per-mode choice.
      if (typeof a === 'number' && typeof b === 'number' && a !== b) return true;
    }
    return false;
  };
  return modes.slice(1).filter((mode) => differs(def.thresholdsByMode[mode] ?? {}));
}

/** Every draft field that describes what the alarm WATCHES, as one comparable key. */
function triggerShape(draft: CustomAlarmDraft): string {
  const conditions = draftConditions(draft);
  return JSON.stringify([
    draft.signalPath ?? '',
    draft.triggerClass ?? '',
    draft.setpoint ?? '',
    draft.clearpoint ?? '',
    draft.setDelaySeconds,
    draft.clearDelaySeconds,
    draft.staleSeconds ?? '',
    conditions === undefined ? null : [conditions.logic, conditions.clauses],
  ]);
}

/**
 * Whether saving this draft would take the alarm outside its recipe.
 *
 * True exactly when something about what it WATCHES moved — the signal, the
 * trigger class, the numbers, the delays, the qualifiers. Name, severity, modes
 * and notification urgency are all things the recipe itself can carry, so
 * changing them keeps the alarm its recipe.
 */
export function leavesRecipe(draft: CustomAlarmDraft, seed: CustomAlarmDraft): boolean {
  return triggerShape(draft) !== triggerShape(seed);
}

/** The PATCH body for a converting save (see {@link toUnlockPatchBody}). */
export interface UnlockPatchBody {
  title?: string;
  severity?: string;
  enabledModes?: string[];
  notificationUrgencyByMode?: Record<string, string>;
  /** Present ONLY on a conversion — its presence is what converts the alarm. */
  custom?: { v: number; path: string; triggerClass: string };
  /** Required alongside `custom`: the recipe's parameter names die with it. */
  thresholdsByMode?: Record<string, Record<string, number>>;
  conditions?: { logic: 'all' | 'any'; clauses: unknown[] } | null;
}

/**
 * The PATCH for saving the unlocked editor — a conversion, or an ordinary recipe
 * edit, decided by {@link leavesRecipe}.
 *
 * An unchanged draft returns `{}` so the caller can skip the request entirely: a
 * no-op PATCH still recompiles and re-delivers the boat's whole ruleset.
 *
 * On the recipe path the trigger fields are deliberately NOT sent. They are
 * expressed in the custom vocabulary (`setpoint`) while the stored alarm holds
 * recipe parameters (`voltsLow`), and writing one over the other would leave the
 * alarm compile-pending on numbers it could not read.
 */
export function toUnlockPatchBody(
  draft: CustomAlarmDraft,
  seed: CustomAlarmDraft,
  original: AlarmDefView
): UnlockPatchBody {
  const patch: UnlockPatchBody = {};
  const title = draft.title.trim();
  if (title.length > 0 && title !== original.title) patch.title = title;
  if (draft.severity !== original.severity) patch.severity = draft.severity;
  const sameModes =
    draft.enabledModes.length === original.enabledModes.length &&
    new Set(original.enabledModes.map((m) => m.toLowerCase())).size ===
      new Set([...draft.enabledModes, ...original.enabledModes].map((m) => m.toLowerCase())).size;
  if (!sameModes) patch.enabledModes = [...draft.enabledModes];

  if (!leavesRecipe(draft, seed)) return patch;

  // --- the conversion ------------------------------------------------------
  const path = draft.signalPath?.trim() ?? '';
  const triggerClass = draft.triggerClass;
  if (path.length === 0 || triggerClass === undefined) return patch;
  patch.custom = { v: 1, path, triggerClass };

  const block: Record<string, number> = {};
  const num = (raw: string | undefined): number | undefined => {
    const trimmed = (raw ?? '').trim();
    if (trimmed.length === 0) return undefined;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : undefined;
  };
  if (triggerClass === 'High' || triggerClass === 'Low') {
    const setpoint = num(draft.setpoint);
    if (setpoint !== undefined) block.setpoint = setpoint;
    const clearpoint = num(draft.clearpoint);
    if (clearpoint !== undefined) block.clearpoint = clearpoint;
  }
  if (triggerClass === 'DataUnavailable') {
    const stale = num(draft.staleSeconds);
    if (stale !== undefined) block.staleMs = Math.round(stale * 1000);
  } else {
    const setDelay = num(draft.setDelaySeconds);
    if (setDelay !== undefined && setDelay > 0) block.setDelayMs = Math.round(setDelay * 1000);
    const clearDelay = num(draft.clearDelaySeconds);
    if (clearDelay !== undefined && clearDelay > 0) {
      block.clearDelayMs = Math.round(clearDelay * 1000);
    }
  }
  // Every enabled mode carries the same block — one rule, one set of numbers (v1).
  const thresholdsByMode: Record<string, Record<string, number>> = {};
  const urgencyByMode: Record<string, string> = {};
  const modes = draft.enabledModes.length > 0 ? draft.enabledModes : ['Underway'];
  for (const mode of modes) {
    thresholdsByMode[mode] = { ...block };
    urgencyByMode[mode] = draft.notify;
  }
  patch.thresholdsByMode = thresholdsByMode;
  patch.notificationUrgencyByMode = urgencyByMode;

  // Sent on every conversion, `null` included: the stored block may hold a recipe
  // preset the API refuses on a custom alarm, so leaving it alone is not an
  // option — it is either replaced by the resolved clauses or explicitly cleared.
  patch.conditions = draftConditions(draft) ?? null;
  return patch;
}

/** Nothing changed — skip the request. */
export function isEmptyUnlockPatch(patch: UnlockPatchBody): boolean {
  return Object.keys(patch).length === 0;
}
